/**
 * **Broadcast-time PURE VIEW** — fog-of-war (GM concealment). Authoritative state, history,
 * undo, persistence, and replay are ALL full-state and untouched by anything in this file —
 * `projectEncounterFor` produces a per-viewer *view* at the wire's edge, never a second state.
 *
 * Everything here is built around one predicate seam, {@link visibleTo}. It is deliberately
 * shaped so the named follow-on plugs in **here**, not as a forked second filter:
 *   - **Computed-LoS FoW** — what a slot sees derived from geometry (`@lancer-vtt/space`'s
 *     `los.ts`/`terrainVisibility`) plus `state`.
 *
 * The body only reads `combatant`/`viewerSlot`/`role` (GM concealment is a per-combatant fact,
 * no geometry needed) — `state` is accepted so the follow-on doesn't reshape the signature.
 *
 * **RAW `Hidden`** (`HIDDEN_STATUS` in `./types`) is an orthogonal *rules* status and
 * NEVER omits a unit, born-Hidden or not: HIDDEN takes away targeting, not knowledge, so the
 * token stays on every board at its exact position (`decisions/0188`, superseding
 * `decisions/0092`'s born-Hidden omission). A GM who wants a genuinely unknown ambusher
 * conceals it.
 */
import type { ActorRole } from "../permissions";
import type {
  ActivationState,
  Combatant,
  CombatantBase,
  CombatantCurrent,
  CombatantId,
  EncounterState,
  SitrepConfig,
} from "./types";
import {
  disclosureOf,
  encounterShowEnemyHp,
  hasFullView,
  sideOf,
} from "./types";

/**
 * **The unit-omission trigger — the one core every omitting surface shares.** True iff
 * `combatant` is `concealed` (GM concealment or a hidden mine). RAW `HIDDEN_STATUS` is
 * deliberately not a trigger (`decisions/0188`).
 *
 * Deliberately **audience-less**: it takes no viewer and carves no GM/controller exemption.
 * Each consumer layers its own audience on top — {@link visibleTo} adds the GM + controllers
 * exemptions for the per-viewer surfaces (the wire, the event-breadcrumb filter, the
 * resolution rider); `encounterRoom.ts`'s `feedOmitted` adds only an existence check, because
 * the durable COMMS feed and the ephemeral decision-card targeting have no per-socket audience
 * to carve an exemption for.
 *
 * **Changing this body changes every omitting surface at once — that is the point**; it is what
 * stops the per-viewer wire and the audience-less surfaces from silently diverging.
 */
export function omissionTriggered(combatant: Combatant): boolean {
  return combatant.concealed === true;
}

/** Named arguments for {@link visibleTo} (kept under the repo's `max-params-3` gate). */
export type VisibleToArgs = {
  combatant: Combatant;
  /** The viewer's slot id, or `null` for a viewer with no owning slot. */
  viewerSlot: string | null;
  role: ActorRole;
  state: EncounterState;
}

/**
 * May the viewer at `viewerSlot` (with `role`) see `combatant` in `state`?
 *
 * Body:
 *   - **GM** ⇒ sees everything, always.
 *   - **Full-view slots** (`hasFullView` — the owner of record `controllerSlotId`, OR a slot
 *     in the visibility-grant list `visibilityGrantSlotIds`) ⇒ see this combatant even if
 *     concealed. For the OWNER that is the incoherence argument: a
 *     GM hiding a player's own unit (or a player's own ambush) from that same player makes no
 *     sense, and owned does **not** imply friendly — `set_controller` can make an ENEMY "a
 *     slot's own unit," so the two categories overlap. For a GRANTEE it is simply what the
 *     grant says: the GM chose to show them this unit.
 *   - **A co-controller is NOT a member** — `decisions/0179`, developer ruling point 6:
 *     visibility is not derived from control. Granting a slot the ability to DRIVE a unit
 *     (`coControllerSlotIds`) reveals nothing about it; seeing its sheet is a separate,
 *     separately-revocable grant. Reaching for `controlsCombatant` here — the predicate this
 *     line used until Phase G — silently re-couples the two axes, which is the exact thing
 *     the ruling forbids. (The stated consequence, accepted: **control without EYES cannot
 *     fire.** A controlled-but-unrevealed unit is omitted from that player's wire entirely
 *     until the view grant lands; the GM affordance grants both by default.)
 *   - **Otherwise** ⇒ hidden iff {@link omissionTriggered} — `combatant.concealed`. That
 *     trigger is the **shared core**, not a local body: `encounterRoom.ts`'s `feedOmitted` calls the same function
 *     for the audience-less surfaces. The two GM/controller exemptions above are this
 *     function's *audience*, and they are the only thing that distinguishes it from the
 *     audience-less consumer.
 *
 * **The owner branch is reachable on a LIVE reassignment, and it's ON THE WIRE.**
 * `controllerSlotId` is not a spawn-only snapshot — the GM-only `set_controller` command
 * reassigns it at any point mid-fight. Because this function feeds `projectEncounterFor`
 * (the per-viewer broadcast), the activity-feed suppression, and the `order`/
 * activation scrubbing, a GM handing a CONCEALED
 * combatant (a hidden mine, a GM-concealed ambusher) to a player puts it on that
 * player's wire immediately — not just their screen. **Ruling: this stays exactly
 * as written, the owner branch stays ABOVE the omission trigger.** You cannot
 * play a unit you cannot see, and the GM is the one deliberately choosing to hand
 * it over through a GM-only command — the same trust boundary `set_concealed`/
 * `set_disclosure` already sit behind. (`CombatWindows.tsx`'s deployer-scoped mine
 * control already assumes owner-visibility, for the same reason.) **The reverse
 * also matters and a GM should know it**: reassigning a unit AWAY from a player
 * who could see it only as its owner silently re-conceals/re-hides it from them —
 * `set_controller`'s inverse makes this automatic and symmetric, so undoing a
 * hand-off also undoes the reveal. See `projectCombatantFor`'s doc for the
 * parallel consequence one layer up (field-level disclosure/`showEnemyHp`).
 * Pinned in `projection.test.ts`'s set_controller-reassignment describe blocks (both
 * this function and that one).
 *
 * **The same is true of a VIEW GRANT, and that is the Phase G addition** (`decisions/0179`):
 * `visibilityGrantSlotIds` is written by the same GM-only `set_controller` command, so a GM
 * granting EYES on a concealed unit puts it on that player's wire
 * immediately, and revoking takes it back off. What changed in Phase G is only *which* list
 * does it: a co-controller used to unmask by extension of the owner rule, and no longer
 * does. Pinned in `projection.test.ts`'s `hasFullView` describe block, whose load-bearing
 * case is the NEGATIVE — a control-only grantee sees nothing.
 *
 * `state` is unused (reserved for the computed-LoS follow-on above) — kept in the
 * signature so extending the predicate body is never a reshape.
 */
export function visibleTo(args: VisibleToArgs): boolean {
  const { combatant, viewerSlot, role } = args;
  if (role === "gm") return true;
  if (hasFullView(combatant, viewerSlot)) return true;
  return !omissionTriggered(combatant);
}

/**
 * The KEEP list, minus `base`/`current` (each masked tier below adds its own partial `base`, and
 * neither masked tier keeps any of `current`) — the fields every masked tier carries verbatim,
 * unredacted: tier-1 identity (`id`/`kind`/`sourceRef`/`tier`/`templateIds`/`name`), board
 * presence (`pos`), ownership and control (`controllerSlotId`, `coControllerSlotIds` — a control
 * grant without the visibility grant leaves the grantee holding a MASKED unit it still commands,
 * `decisions/0179`, so the turn chip needs the co-list to offer GO/END and the YOU tag), the fog-of-war axis (`concealed` —
 * orthogonal, stacks with disclosure), and observable battlefield conditions (`statuses`, which
 * are visible at every disclosure level).
 */
type MaskedKeepFields = Pick<
  Combatant,
  | "id"
  | "kind"
  | "pos"
  | "sourceRef"
  | "tier"
  | "templateIds"
  | "name"
  | "controllerSlotId"
  | "coControllerSlotIds"
  | "concealed"
  | "statuses"
>;

/**
 * The `"class"`-tier partial base: board footprint (`size`) + side (`allegiance`), plus the rows
 * of any feature this unit has revealed by using it (`decisions/0207`,
 * {@link Combatant.revealedFeatureIds}). Each of `weapons` / `equippedSystems` / `npcFeatures` is
 * present only when it holds at least one revealed row, and holds exactly those rows, whole, as
 * the `"loadout"` tier would show them.
 *
 * This tier is a **render-time** shape, not a wire shape — it is only ever produced by
 * {@link projectForDisplay} for the render chokepoints, all of whose masked branches are
 * early returns. Notably it need NOT carry `objectProfile`: the player's spatial consumers
 * (`movement.ts`'s `placements()` and `objectContributions()`'s `contributesToTerrain`) read the
 * raw broadcast state, which carries every field. `packages/rules/src/encounter/
 * wallOccupancyProjection.test.ts` is the proof.
 */
export type ClassTierBase = Pick<CombatantBase, "size" | "allegiance"> &
  Partial<Pick<CombatantBase, "equippedSystems" | "weapons" | "npcFeatures">>;

/** The `"loadout"`-tier partial base: the class tier plus weapons/systems/mounts — still no stat block. */
export type LoadoutTierBase = ClassTierBase &
  Pick<CombatantBase, "mounts" | "equippedSystems" | "weapons" | "npcFeatures">;

/**
 * A hostile combatant redacted to the `"class"` disclosure tier — the stat block, the
 * loadout, and all of `current` are simply ABSENT (a real `Pick`-built partial base), not a
 * full `CombatantBase` with its ~20 info-bearing fields nulled out.
 */
export type ClassMaskedCombatant = MaskedKeepFields & {
  disclosure: "class";
  base: ClassTierBase;
  /**
   * The COUNT of weapons/features a still-locked THREAT/TRAITS/SYSTEMS/REACTIONS/TECH section
   * would reveal, without revealing their identity. Revealed rows (`base.weapons` /
   * `base.npcFeatures`, `decisions/0207`) are not counted: this is what is still locked — `WEAPONS ▪ 3 ▪ LOCKED` rather than
   * `WEAPONS ▪ LOCKED`, because a lock with a shape is informative and creates the pull. This is
   * a **deliberate, one-field widening of this masking function** — never read around it (a
   * client must not re-derive this by peeking at `state.combatants` itself). It is a house grant
   * beyond RAW's class+template floor, reversible by deleting this field. `features` is a single
   * aggregate across all four
   * `npcFeatures` kinds (Trait/System/Reaction/Tech) — this function has no reason to expose a
   * per-kind breakdown a `"class"`-tier player can't otherwise place, and the ruled shape is
   * exactly `{weapons, features}`, not four separate counts.
   *
   * **`"loadout"`-tier does NOT carry this field** (see {@link LoadoutMaskedCombatant}) —
   * deliberate, not an oversight: every section this field could inform (THREAT/TRAITS/SYSTEMS/
   * REACTIONS/TECH) is already REVEALED at `"loadout"`, with `base.weapons`/`base.npcFeatures`
   * present in full, so a caller derives the same numbers via `.length` directly. A parallel
   * `redactedCounts` there would be a second, driftable source of a number the real arrays
   * already carry — and the one section still locked at `"loadout"` (VITALS) isn't a countable
   * quantity the way weapons/features are.
   */
  redactedCounts: { weapons: number; features: number };
};

/**
 * A hostile combatant redacted to the `"loadout"` disclosure tier — adds
 * weapons/systems/mounts over the `"class"` tier; the stat block and `current` stay absent.
 * **Deliberately carries no `redactedCounts`** — see {@link ClassMaskedCombatant}'s doc on that
 * field for why.
 */
export type LoadoutMaskedCombatant = MaskedKeepFields & {
  disclosure: "loadout";
  base: LoadoutTierBase;
};

/**
 * The explicit typed masked variant {@link projectCombatantFor} emits for a `"class"`- or
 * `"loadout"`-disclosed hostile — never a full `CombatantBase` with nulled fields. **The client
 * discriminates a masked combatant from a real one via `current`'s ABSENCE**
 * (`frontend/src/lib/encounter/disclosure.ts`'s `isMaskedCombatant`), **not** `"disclosure" in x`
 * — `Combatant.disclosure` is itself an ordinary optional field on the real, unmasked type, so a
 * genuinely full-disclosed hostile also satisfies that check; only `current`'s presence/absence
 * cleanly separates the two (a `MaskedCombatant` never carries `current` at all, a real `Combatant`/
 * `HpHiddenCombatant` always does).
 */
export type MaskedCombatant = ClassMaskedCombatant | LoadoutMaskedCombatant;

/**
 * A `"full"`-disclosure hostile with its live `current.hp` redacted (`showEnemyHp` off).
 * Identical to `Combatant` except `current` omits `hp`; the frozen base (max HP, loadout, …)
 * is untouched — the gate covers only the transient current-HP number, never the
 * snapshot-faithful base. Presence of `hp` on `current` IS the signal (mirrors `CombatantBase.maxHeat`'s
 * absence-is-the-signal convention), read via `"hp" in x.current`.
 */
export type HpHiddenCombatant = Omit<Combatant, "current"> & { current: Omit<CombatantCurrent, "hp"> };

/**
 * What {@link projectCombatantFor} emits for one combatant: the real, untouched `Combatant`
 * (GM/owner/ally, or an explicitly `"full"`-disclosed hostile with `showEnemyHp` on —
 * reference-identity preserved in every one of those cases), an {@link HpHiddenCombatant}
 * (`"full"`-disclosed, `showEnemyHp` off), or a {@link MaskedCombatant} tier
 * (`"class"`/`"loadout"`-disclosed). The client switches on the typed shape.
 */
export type ProjectedCombatant = Combatant | HpHiddenCombatant | MaskedCombatant;

/**
 * One preselectable pre-roll `choose` prompt a weapon instance would raise — the discovery seam.
 * Surfaced so an inspector UI can offer the pick **before** target selection, as a fast-path over
 * the mid-resolution `choose` prompt (which remains the general fallback for anything not
 * projected here — see {@link ProjectedEncounterState.preRollChoices}'s own doc).
 */
export type PreRollChoicePrompt = {
  /**
   * The `choose` atom's stable prompt id — **byte-equal** to the key
   * {@link "@lancer-vtt/rules".interpretChoose} looks up in `RequestAttack.choices` at
   * resolve time for the same carrier (both derive from the authored atom's own `prompt`
   * string). This equality is what lets a preselected answer here suppress the live
   * prompt: populate `RequestAttack.choices[promptId] = <option id>` before dispatch and
   * the interpreter finds it already answered.
   */
  promptId: string;
  /** Human-readable prompt text (mirrors `promptId` today — a `choose` atom carries no separate label). */
  prompt: string;
  /** The selectable options, in authored order. */
  options: { id: string; label: string }[];
};

/**
 * One `requiresOptIn` "may" rider a weapon instance could raise — a pre-declared checkbox,
 * **not** a `choose`-atom prompt. This
 * is a deliberate SIBLING of {@link PreRollChoicePrompt}, not a widening of it: a `choose`
 * prompt's `promptId` round-trips byte-equal into `RequestAttack.choices[promptId]` as an
 * *option id* answer, whereas an opt-in rider answers a structurally different key
 * (`"optIn:" + name` → the literal string `"true"`, never an option id) — see
 * `PreRollChoicePrompt`'s own doc for that contract and `atoms.ts`'s `TriggeredEffect.
 * requiresOptIn` doc for the answer-key mechanism. Also unlike `PreRollChoicePrompt` (which is
 * discovered by re-running `on_attack_declared` and can only see attacker+weapon-predicate-
 * reachable prompts), this rider is a **standing conditional intent** ("if it lands and
 * qualifies, do it") — it is surfaced whenever the attacker+weapon combination COULD raise it
 * (weapon `match` admits the weapon), without predicting whether it will actually fire; the
 * engine's own `condition` + outcome-dependent `match` facets settle that at real fire time.
 */
export type OptInRiderPrompt = {
  /** The full recorded-choice answer key (`"optIn:" + name`) — the client writes this key with value `"true"`/absent, never reconstructing it by string-concatenation. */
  key: string;
  /** The clause's own `name` (also the `key`'s suffix). */
  name: string;
  /** Human-readable label, humanized from `name` (an authored label is a possible follow-up). */
  label: string;
  /** The authored initial checkbox state (`TriggeredEffect.optInDefault`) — what the UI PRE-FILLS, never what the engine assumes (the fail-closed default is unconditional). */
  default: boolean;
};

/**
 * One `requiresOptIn` "may" rider a combatant's `on:"action"` clause could raise —
 * field-for-field identical to
 * {@link OptInRiderPrompt} but kept as its own named type rather than a reuse, because the two
 * are keyed by structurally different things (weapon instance vs. action id — see
 * {@link ProjectedEncounterState.optInActionRiders}'s own doc) and a future divergence in
 * either shape should not have to un-conflate a shared type first. Boost's Flying riders
 * (`pg_mobility_hardsuit`/`ms_rapid_burst_jump_jet_system`) are the first content to populate
 * this.
 */
export type OptInActionRiderPrompt = {
  /** The full recorded-choice answer key (`"optIn:" + name`) — same contract as {@link OptInRiderPrompt.key}. */
  key: string;
  /** The clause's own `name` (also the `key`'s suffix). */
  name: string;
  /** Human-readable label, humanized from `name` (same derivation as {@link OptInRiderPrompt.label}). */
  label: string;
  /** The authored initial checkbox state (`TriggeredEffect.optInDefault`) — same contract as {@link OptInRiderPrompt.default}. */
  default: boolean;
};

/**
 * The wire-honest counterpart to {@link EncounterState}: identical to `EncounterState` except
 * `combatants` maps to {@link ProjectedCombatant}, not the bare `Combatant`, so a client reading
 * a per-viewer projected state can exhaustively switch on the masked/HP-hidden/real shape
 * WITHOUT an unchecked cast. `EncounterState` (a real, unredacted state — the GM's own view) is
 * a proper subtype and needs no cast to satisfy this type — `Combatant` is one member of the
 * `ProjectedCombatant` union, so the widening is free.
 */
export type ProjectedEncounterState = Omit<EncounterState, "combatants"> & {
  combatants: Record<CombatantId, ProjectedCombatant>;
  /**
   * **Ephemeral** pre-roll-choices sidecar — which
   * `choose` prompts each visible combatant's weapons would raise, keyed
   * `combatantId → weaponInstanceKey (see {@link weaponInstanceKey}) → prompts[]`. Computed
   * by `backend`'s `EncounterRoom` on **every** broadcast (a snapshot projection, not a lazy
   * per-arm query) by reusing the
   * engine's `on_attack_declared` gather (`@lancer-vtt/rules`'s `previewAttackDeclaredChoice`)
   * over each combatant's frozen weapons — **never** persisted, **never** logged, and
   * **never** attached to `CombatantBase.weapons` itself (that base is spawn-frozen and part
   * of the replay forward-log; a derived projection there would corrupt it). Absent /
   * `undefined` for a caller that never computes it (a bare `EncounterState` widened into this
   * type) — reads as "nothing projected," identical to an
   * empty map. Only `answerKind:"options"` pre-roll choices reachable from attacker+weapon
   * predicates alone are projected here; anything target-gated (or a `"direction"` spatial
   * pick) is absent and falls through to the ordinary live `needs_decision` prompt.
   */
  preRollChoices?: Record<CombatantId, Record<string, PreRollChoicePrompt[]>>;
  /**
   * **Ephemeral** opt-in-rider sidecar — which
   * `requiresOptIn` riders each visible combatant's weapons could raise, keyed
   * `combatantId → weaponInstanceKey → riders[]`. Computed by `backend`'s `EncounterRoom`
   * alongside {@link preRollChoices} (same broadcast, same defensive per-weapon try/catch,
   * same never-persisted/never-logged/never-folded-onto-`CombatantBase.weapons` contract) by
   * reusing the engine's own attack-family gather (`@lancer-vtt/rules`'s `previewOptInRiders`)
   * rather than a parallel scan over the overlay. Absent / `undefined` for a caller that never
   * computes it — reads as "nothing projected," identical to an empty map.
   */
  optInRiders?: Record<CombatantId, Record<string, OptInRiderPrompt[]>>;
  /**
   * **Ephemeral** action-family opt-in-rider sidecar — the action-family SIBLING of
   * {@link optInRiders}, not a widening of it.
   * `optInRiders`' per-weapon keying (`weaponInstanceKey`) is documented and load-bearing for
   * ITS carriers (`on_hit`/`on_crit` clauses that predicate on which weapon fired); an
   * `on:"action"` clause (Boost's Flying riders) carries no weapon at all, so widening
   * `optInRiders` to also accept an action id would either invent a fake weapon key or make the
   * map's key type a lie. This field is therefore keyed
   * **`combatantId → actionId → riders[]`** — the action catalog id (`"boost"`, …), the SAME id
   * `spend_action.actionId`/`ResolveActionTakenTriggerArgs.actionId` use — **never** a
   * `weaponInstanceKey`. Computed by `backend`'s `EncounterRoom` alongside {@link
   * preRollChoices}/{@link optInRiders} (same broadcast, its OWN defensive per-carrier
   * try/catch — a discovery bug in one carrier's authored content must never break the whole
   * broadcast, same contract as the other two sidecars — never persisted, never logged, never
   * folded onto `CombatantBase`) by reusing the engine's own action-family gather
   * (`@lancer-vtt/rules`'s `previewOptInActionRiders`, which itself reuses `gatherHandlersAt` —
   * the SAME call `resolveActionTakenTrigger` fires with, so this enumeration can never drift
   * from what actually fires). Absent / `undefined` for a caller that never computes it — reads
   * as "nothing projected," identical to an empty map.
   */
  optInActionRiders?: Record<CombatantId, Record<string, OptInActionRiderPrompt[]>>;
  /**
   * **Ephemeral** state-selected firing profile per weapon, keyed `combatantId →
   * weaponInstanceKey → profile id`. Present only for a weapon whose overlay entry carries a
   * `select` clause (the Assault Cannon's Spin-Up Mode, the Apocalypse Rail's Charge Rail): its
   * profile is set by state, not picked, and the room refuses a named profile that disagrees
   * (`@lancer-vtt/rules` `checkProfileByState`). The profile pills read it to show the live mode
   * and lock the rest. Computed by `backend`'s `EncounterRoom` with the other sidecars; never
   * persisted. Absent reads as "every profile is a free pick".
   */
  activeProfiles?: Record<CombatantId, Record<string, string>>;
  /**
   * **Ephemeral** weapon keys, per combatant, whose overlay entry carries the weapon-own
   * `sparesAllies` facet (the Veil Rifle): the area fan-out doesn't attack the wielder's own side.
   * The AoE hit preview reads it (`attackAffectedCombatantKeys`' `sparesAllies`) so a caught ally
   * shows as spared. Computed with the other sidecars; never persisted.
   */
  sparingWeapons?: Record<CombatantId, string[]>;
};

/** Named arguments for {@link projectCombatantFor} (kept under the repo's `max-params-3` gate). */
export type ProjectCombatantArgs = {
  combatant: Combatant;
  /** The viewer's slot id, or `null` for a viewer with no owning slot. */
  viewerSlot: string | null;
  role: ActorRole;
  state: EncounterState;
}

/**
 * Redact `combatant` for one viewer — the
 * FIELD-level counterpart to {@link visibleTo}'s unit-level omission; the two gates stack and
 * never conflate (concealment omits the whole unit, disclosure redacts fields of a unit that
 * IS visible).
 *
 * - **GM** ⇒ identity, always (reference-identity preserved).
 * - **A full-view slot** (`hasFullView`: the owner of record, OR a slot in
 *   `visibilityGrantSlotIds`) ⇒ identity, always — disclosure never hides a slot's own unit
 *   from itself, and a view grant is the GM saying "show them this one." Mirrors
 *   `visibleTo`'s exemption, and like it, **a co-controller is deliberately NOT a member**
 *   (`decisions/0179`): a control grant does not unmask, so a granted driver of a
 *   `disclosure: "class"` hostile still sees the masked tier until EYES is granted too.
 * - **An ally** (`sideOf(combatant) !== "npc"` from a player viewer) ⇒ identity, always —
 *   redaction only ever targets a HOSTILE from a player's perspective; allies/own units are
 *   already fully visible.
 * - **A hostile's `disclosure` level** (`disclosureOf` — the RAW default: a never-explicitly-set
 *   combatant reads as `"class"`, not identity) ⇒ `"full"` redacts only `current.hp` (iff
 *   `!encounterShowEnemyHp(state)`); `"class"`/`"loadout"` emit the matching
 *   {@link MaskedCombatant} tier.
 *
 * **{@link projectEncounterFor} does NOT call this** — field-masking is display-only, off the
 * wire entirely, so the broadcast carries every visible combatant's full, unredacted fields.
 * This logic runs at RENDER time instead, via {@link projectForDisplay} (the client's one entry
 * point, see its own doc for the brand this mints).
 *
 * **The owner branch is reachable on a LIVE reassignment, same as {@link visibleTo}'s (see that
 * doc for the full rationale — it applies here identically).** `controllerSlotId` is not a
 * spawn-only snapshot; the GM-only `set_controller` command reassigns it mid-fight.
 * Concretely, handing a HOSTILE combatant to a player **overrides both `set_disclosure`
 * and `showEnemyHp` for that player**: a `disclosure: "class"`/`"loadout"`-masked
 * NPC unmasks to its full stat block the instant it becomes theirs, and a
 * `disclosure: "full"` NPC's HP stops being hidden even when `showEnemyHp` is off.
 * **Ruling: this stays exactly as written** — the owner branch stays above the
 * disclosure ladder, for the identical reason `visibleTo` does (you cannot play a
 * unit whose stat block you cannot see). **The reverse also matters**: reassigning
 * a unit AWAY from a player silently re-masks/re-hides whatever this branch had
 * been exempting for them — a GM should expect BOTH directions, not just the grant.
 * Pinned in `projection.test.ts`'s set_controller-reassignment describe block.
 *
 * **A VIEW GRANT reaches this branch identically** (`decisions/0179`): a slot in
 * `visibilityGrantSlotIds` sees the full stat block of a `disclosure`-masked or HP-hidden
 * hostile, and a revoke re-masks it. A CONTROL grant (a co-controller entry) does **not** —
 * that coupling was removed by the developer's ruling, and the accepted consequence is that a
 * granted driver of a masked hostile can activate and end its turn but cannot reach its
 * weapons until EYES is granted too. Pinned in `projection.test.ts`'s `hasFullView` block.
 */
export function projectCombatantFor(args: ProjectCombatantArgs): ProjectedCombatant {
  const { combatant, viewerSlot, role, state } = args;
  if (role === "gm") return combatant;
  if (hasFullView(combatant, viewerSlot)) return combatant;
  if (sideOf(combatant) !== "npc") return combatant;

  const level = disclosureOf(combatant);

  if (level === "full") {
    if (encounterShowEnemyHp(state)) return combatant;
    const { hp: _hp, ...current } = combatant.current;
    return { ...combatant, current };
  }

  const keep: MaskedKeepFields = {
    id: combatant.id,
    kind: combatant.kind,
    pos: combatant.pos,
    sourceRef: combatant.sourceRef,
    tier: combatant.tier,
    templateIds: combatant.templateIds,
    name: combatant.name,
    controllerSlotId: combatant.controllerSlotId,
    coControllerSlotIds: combatant.coControllerSlotIds,
    concealed: combatant.concealed,
    statuses: combatant.statuses,
  };
  // ── THE CLASS-TIER STRIP, and its twin in the resolution record ──────────────────────────
  // `classBase` keeps `size` + `allegiance`; the `loadout` branch just below adds back
  // `mounts`/`equippedSystems`/`weapons`/`npcFeatures` whole, and the class branch adds back only
  // the rows whose id is in `revealedFeatureIds` (`decisions/0207`). This is the AUTHORITY for
  // "what does the class tier hide" — and `undisclosedSourceNames` (`./resolutionRedaction.ts`)
  // enumerates the same unrevealed rows so a recorded `accuracyContributions[].name` /
  // `damage.source` naming one of them is replaced with a sentinel before broadcast. **Change
  // one, change the other**: a newly-stripped field that the resolution record can name would
  // leak there silently. `resolutionRedaction.test.ts` pins the pairing.
  const classBase: ClassTierBase = { size: combatant.base.size, allegiance: combatant.base.allegiance };
  if (level === "loadout") {
    const base: LoadoutTierBase = {
      ...classBase,
      mounts: combatant.base.mounts,
      equippedSystems: combatant.base.equippedSystems,
      weapons: combatant.base.weapons,
      npcFeatures: combatant.base.npcFeatures,
    };
    return { ...keep, disclosure: "loadout", base };
  }
  const revealed = new Set(combatant.revealedFeatureIds ?? []);
  const weapons = (combatant.base.weapons ?? []).filter((w) => revealed.has(w.id));
  const equippedSystems = (combatant.base.equippedSystems ?? []).filter((sys) => revealed.has(sys.id));
  const npcFeatures = (combatant.base.npcFeatures ?? []).filter((f) => f.id !== undefined && revealed.has(f.id));
  const base: ClassTierBase = {
    ...classBase,
    ...(weapons.length > 0 ? { weapons } : {}),
    ...(equippedSystems.length > 0 ? { equippedSystems } : {}),
    ...(npcFeatures.length > 0 ? { npcFeatures } : {}),
  };
  const redactedCounts = {
    weapons: (combatant.base.weapons?.length ?? 0) - weapons.length,
    features: (combatant.base.npcFeatures?.length ?? 0) - npcFeatures.length,
  };
  return { ...keep, disclosure: "class", base, redactedCounts };
}

/**
 * A `unique symbol` brand marking a {@link ProjectedCombatant} that has been through
 * {@link projectForDisplay} — the compile-time guard on the render boundary. Structural typing
 * gives that boundary nothing here: `ProjectedCombatant = Combatant
 * | HpHiddenCombatant | MaskedCombatant` has the raw, full `Combatant` as a union member, so a prop
 * typed plainly `ProjectedCombatant` accepts an unprojected value and typechecks clean — silently
 * rendering full enemy stats to a player. The brand converts that into a compile error.
 *
 * **Minted in exactly one place — {@link projectForDisplay} — and nowhere else. No casts to this
 * type anywhere outside that function.** Accepted only by the render chokepoints:
 * `InspectorBody`/`NpcInspectorBody` (`frontend/src/lib/encounter/CombatWindows.tsx`) and
 * `tokenChromeFor` (`frontend/src/lib/encounter/tokenChrome.ts`, the board's per-token vitals; it
 * replaced the TURN rail's `turnChipSections`, `decisions/0190`), and `UnitIntelCard`
 * (`frontend/src/lib/encounter/UnitIntelCard.tsx`, hover intel's stat card). Helpers between a raw combatant
 * and those keep taking `Combatant`/`ProjectedCombatant` — the brand lives at the component boundary,
 * not threaded up the call graph. If it ever wants to propagate past one hop from a mint site, the
 * chokepoint analysis was wrong; re-derive rather than widening the brand.
 */
declare const DISPLAY: unique symbol;
export type DisplayCombatant = ProjectedCombatant & { readonly [DISPLAY]: true };

/**
 * The client's ONE entry point for render-time masking — mints a {@link DisplayCombatant} by
 * running the exact same field-redaction {@link projectCombatantFor} does (GM/owner/ally
 * identity, disclosure-tier stripping, the `showEnemyHp` HP gate). The wire carries every
 * visible combatant's full data (`projectEncounterFor` redacts no fields) — this is what turns
 * that full data back into "what THIS viewer may see," at the render boundary instead of the
 * broadcast boundary. A thin wrapper, not a reimplementation: the redaction rules live once, in
 * `projectCombatantFor`.
 */
export function projectForDisplay(args: ProjectCombatantArgs): DisplayCombatant {
  return projectCombatantFor(args) as DisplayCombatant;
}

/** Remove every key in `concealedIds` from `record`, preserving the rest (pure, no mutation). */
function omitConcealedKeys<V>(
  record: Record<CombatantId, V>,
  concealedIds: ReadonlySet<CombatantId>,
): Record<CombatantId, V> {
  if (concealedIds.size === 0) return record;
  const result: Record<CombatantId, V> = {};
  for (const [id, v] of Object.entries(record)) {
    if (!concealedIds.has(id)) result[id] = v;
  }
  return result;
}

/**
 * Scrub `concealedIds` out of the activation's **per-unit records** (`activated`,
 * `reactionSlot`, `reactionUses`, `activationsUsed`, `actionRoundUses`, `moveReactionDeclines`, `splitMove`) — the breadcrumbs a
 * player's client could otherwise read to learn a concealed combatant's id even though it's
 * absent from `combatants`. `activationsUsed` and `actionRoundUses` are per-unit id-keyed round
 * tallies minted alongside `reactionUses` — scrubbed the same way, via the same
 * {@link omitConcealedKeys} helper.
 *
 * Deliberately does NOT touch `turn` — `turn.actor` can never name a concealed combatant, because
 * `activate_unit` on a concealed combatant auto-reveals it first (two-phase alternation is public
 * bookkeeping players count, and a secret activation would corrupt the turn structure). That
 * auto-reveal guard lives elsewhere; nothing in this pure projection needs to defend against it.
 */
function scrubActivation(activation: ActivationState, concealedIds: ReadonlySet<CombatantId>): ActivationState {
  if (concealedIds.size === 0) return activation;
  const { splitMove, ...rest } = activation;
  const split = splitMove !== undefined && ![splitMove.moverId, splitMove.shooterId, ...(splitMove.trampled ?? [])].some((id) => concealedIds.has(id)) ? { splitMove } : {};
  return {
    ...rest,
    ...split,
    activated: activation.activated.filter((id) => !concealedIds.has(id)),
    reactionSlot: omitConcealedKeys(activation.reactionSlot, concealedIds),
    reactionUses: omitConcealedKeys(activation.reactionUses, concealedIds),
    activationsUsed: omitConcealedKeys(activation.activationsUsed, concealedIds),
    actionRoundUses: omitConcealedKeys(activation.actionRoundUses, concealedIds),
    ...(activation.moveReactionDeclines !== undefined
      ? { moveReactionDeclines: activation.moveReactionDeclines.filter((d) => !concealedIds.has(d.watcherId) && !concealedIds.has(d.moverId)) }
      : {}),
  };
}

/**
 * Redact `sitrep` for one non-GM viewer (Core p.273's Recon
 * True CZ) — the sitrep-level counterpart to {@link projectCombatantFor}'s combatant-level
 * redaction. `SitrepZone.hidden` marks Recon's secretly-designated True CZ (`types.ts`'s
 * doc on the field) — a GM-only concealed fact, mirroring the `concealed`/`set_concealed`
 * GM-only manual-tier pattern. Strips `hidden` off every zone (by omitting the key, not
 * setting it `false` — a player must never learn "not this one" any more than "this one")
 * so a player sees four indistinguishable Control Zones (ids/kinds/rects/side all
 * untouched) with no tell for which is the True CZ. Everything else about the sitrep
 * (type, roundLimit, objectiveId) is legitimately player-visible and passes through.
 *
 * Pure identity (reference-equal) when there's nothing to strip — no `sitrep`, or no zone
 * flagged `hidden` (every non-Recon sitrep, and a Recon sitrep before/without a True-CZ
 * designation) — load-bearing for {@link projectEncounterFor}'s own identity-return gate.
 */
function projectSitrepFor(sitrep: SitrepConfig | undefined): SitrepConfig | undefined {
  if (!sitrep) return sitrep;
  if (!sitrep.zones.some((z) => z.hidden)) return sitrep;
  return {
    ...sitrep,
    zones: sitrep.zones.map((z) => {
      if (!z.hidden) return z;
      const { hidden: _hidden, ...rest } = z;
      return rest;
    }),
  };
}

/**
 * Project `state` for one viewer — the broadcast-time pure view fog-of-war plugs into
 * `welcomeMessage`/the per-socket `changeMessageFor` seam. Drops every combatant
 * {@link visibleTo} denies, scrubs their ids out of `order` and the activation's per-unit
 * records (`scrubActivation`), redacts Recon's hidden True CZ per {@link projectSitrepFor},
 * and otherwise returns `state` unchanged.
 *
 * **Does NO per-combatant FIELD redaction.** The wire ships the full data and the client displays
 * only what has been revealed — a private server for friends, where adversarial devtools reading
 * is accepted. See {@link projectForDisplay}, the client's call site for the same
 * {@link projectCombatantFor} logic. This function still omits whole combatants
 * ({@link visibleTo} concealment), still scrubs `order` and the
 * activation breadcrumbs, and still redacts the sitrep's hidden True CZ.
 *
 * **GM/identity projection is pure identity** — returns the exact same `state` reference,
 * never a reconstructed copy, so a GM's view is provably the untouched authoritative state
 * (this is also how the GM keeps seeing the real `hidden:true` designation — the sitrep
 * redaction below never runs for a GM viewer).
 *
 * **Identity when nothing is concealed AND nothing is redacted** — if no combatant is denied AND
 * `projectSitrepFor` returns `state.sitrep` untouched (reference-equal), returns `state` unchanged
 * (reference-equal) rather than a structurally-equal rebuild. This is what keeps projection
 * invisible to every existing suite (no concealed combatants and no Recon `hidden` zone ⇒ every
 * call site sees the identical state it always did). **The sitrep check must be folded into this
 * same gate, not checked separately** — a Recon sitrep with a hidden True CZ and ZERO concealed
 * combatants would otherwise hit a bare `concealedIds.size === 0` short-circuit and return the raw
 * `state` wholesale, leaking `sitrep.zones[*].hidden` straight through.
 *
 * **The wire type is {@link ProjectedEncounterState}.** `EncounterState` widens into it for free
 * (no cast) since `Combatant` is one member of the {@link ProjectedCombatant} union — a
 * GM/identity return of the untouched `state` is still a valid `ProjectedEncounterState`, and so
 * is EVERY non-GM viewer's, since nothing here mints a `MaskedCombatant`/`HpHiddenCombatant`.
 * `sitrep`
 * is inherited unchanged from `EncounterState` (via `Omit<EncounterState, "combatants">`), so the
 * redacted `SitrepConfig` {@link projectSitrepFor} emits needs no distinct wire shape — it's a
 * real `SitrepConfig`, just missing the `hidden` marker on any zone.
 */
export function projectEncounterFor(state: EncounterState, viewerSlot: string | null, role: ActorRole): ProjectedEncounterState {
  if (role === "gm") return state;

  const concealedIds = new Set<CombatantId>();
  for (const c of Object.values(state.combatants)) {
    if (!visibleTo({ combatant: c, viewerSlot, role, state })) concealedIds.add(c.id);
  }

  const sitrep = projectSitrepFor(state.sitrep);
  const sitrepRedacted = sitrep !== state.sitrep;

  if (concealedIds.size === 0 && !sitrepRedacted) return state;

  const combatants: Record<CombatantId, ProjectedCombatant> = {};
  for (const [id, c] of Object.entries(state.combatants)) {
    if (concealedIds.has(id)) continue;
    combatants[id] = c;
  }

  return {
    ...state,
    sitrep,
    combatants,
    order: state.order.filter((id) => !concealedIds.has(id)),
    activation: scrubActivation(state.activation, concealedIds),
  };
}
